import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n2 } from "../../lib/format";
import { Modal, Notice, Pager } from "../../lib/ui";

/*
 * Fixed assets list – port of assets.php + assets_ajax.php (API: GET /api/assets, backend/assets.py)
 * Server-side search (name, type, location), sorting and paging, 10 per page.
 * Update/Delete buttons are not shown yet: update_asset.php and delete_asset.php haven't been ported.
 */

const COLS = [["asset_id", "ID"], ["asset_name", "Asset name"], ["asset_type", "Type"], ["purchase_date", "Purchase date"],
  ["purchase_cost", "Purchase cost", true], ["depreciation_method", "Depreciation"], ["useful_life_years", "Useful life (yrs)", true],
  ["salvage_value", "Salvage value", true], ["current_value", "Current value", true], ["location", "Location"], ["status", "Status"]];
const MONEY = new Set(["purchase_cost", "salvage_value", "current_value"]);

export default function Assets({ user }) {
  const admin = user?.role === "admin";
  const [del, setDel] = useState(null);
  const [notice, setNotice] = useState(null);
  const api = useApi();
  const [q, setQ] = useState({ search: "", sort: "asset_id", order: "asc", page: 1 });
  const [term, setTerm] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async (p) => {
    try { setData(await api.get("/api/assets", p)); setError(null); } catch (e) { setError(e.message); }
  }, [api]);
  useEffect(() => { load(q); }, [load, q]);
  useEffect(() => { const t = setTimeout(() => setQ((s) => (s.search === term ? s : { ...s, search: term, page: 1 })), 300); return () => clearTimeout(t); }, [term]);

  const sortBy = (k) => setQ((s) => ({ ...s, sort: k, order: s.sort === k && s.order === "asc" ? "desc" : "asc", page: 1 }));

  return (
    <main className="pg" style={{ maxWidth: "96rem" }}>
      <PageHeader slug="assets" actions={<Link to="/asset_register" className="btn primary"><i className="fas fa-plus" /> Add fixed asset</Link>} />
      <Notice notice={notice} onClose={() => setNotice(null)} />
      <Notice notice={error && { tone: "err", text: error }} />
      <section className="panel">
        <div className="toolbar"><div className="field grow"><div className="search"><i className="fas fa-magnifying-glass" />
          <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search name, type or location" aria-label="Search assets" /></div></div>
          {data && <span style={{ fontSize: ".8125rem", color: "var(--muted)", alignSelf: "center" }}>{data.total} asset{data.total === 1 ? "" : "s"}</span>}
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr>{COLS.map(([k, label, num]) => (
              <th key={k} className={`sortable ${num ? "num" : ""}`} onClick={() => sortBy(k)} aria-sort={q.sort === k ? (q.order === "asc" ? "ascending" : "descending") : "none"}>
                {label} {q.sort === k && <i className={`fas ${q.order === "asc" ? "fa-sort-up" : "fa-sort-down"}`} />}</th>))}{admin && <th />}</tr></thead>
            <tbody>
              {data?.rows.map((r) => (
                <tr key={r.asset_id}>{COLS.map(([k, , num]) => (
                  <td key={k} className={num ? "num mono" : ""} style={k === "asset_name" ? { fontWeight: 600, color: "var(--ink)" } : undefined}>
                    {k === "status" ? <span className={`pill-tag ${r.status === "In Use" ? "ok" : r.status === "Under Repair" ? "warn" : r.status === "Disposed" ? "err" : ""}`}>{r.status}</span>
                      : MONEY.has(k) ? n2(r[k]) : r[k] ?? "–"}</td>))}
                  {admin && <td style={{ whiteSpace: "nowrap" }}><Link className="btn sm" to={`/update_asset?id=${r.asset_id}`}>Update</Link>{" "}
                    <button type="button" className="btn sm" aria-label={`Delete ${r.asset_name}`} onClick={() => setDel(r)}><i className="fas fa-trash-can" /></button></td>}</tr>
              ))}
            </tbody>
          </table>
          {data && data.rows.length === 0 && <div className="empty"><i className="fas fa-building" />No assets found.</div>}
        </div>
        {data && <Pager page={data.page} totalPages={data.pages} onPage={(p) => setQ({ ...q, page: p })} />}
      </section>
      {del && <Modal title="Delete this asset?" onClose={() => setDel(null)} actions={<><button type="button" className="btn" onClick={() => setDel(null)}>Cancel</button>
        <button type="button" className="btn danger" onClick={async () => { const a = del; setDel(null);
          try { const r = await api.request(`/api/assets/${a.asset_id}`, "DELETE"); setNotice({ tone: r.success ? "ok" : "err", text: r.message }); setQ({ ...q }); }
          catch (e) { setNotice({ tone: "err", text: e.message }); } }}>Delete</button></>}>
        <b>{del.asset_name}</b> will be removed from the asset register. If it was sold or scrapped, consider setting its status to Disposed instead, which keeps its history.</Modal>}
    </main>
  );
}
